<template>
  <li class="deck-thumbnail-tile" :data-testid="`deck-tile-${slide.id}`">
    <EditorDeckHeaderTile
      v-if="slide.type === 'H' && !isGridView"
      :slide="slide"
      :selected="selected"
      :disabled="isDragging"
      @click="$emit('click', $event)"
      @contextmenu="$emit('contextmenu', $event)"
      @shift-click="$emit('shift-click:title', $event)"
      @ctrl-click="$emit('ctrl-click:title', $event)"
      @create-header-slide="$emit('create-header-slide', $event)"
      @cancel-create-header-slide="$emit('cancel-create-header-slide', $event)"
      @update:title="$emit('update:title', $event)"
      @focus:title="$emit('focus:title')"
      @blur:title="$emit('blur:title')"
    />
    <span v-else-if="slide.type === 'T'" data-testid="deck-tile-type-t" />
    <EditorDeckThumbnailTile
      v-else
      class="relative slide"
      :item="slide"
      :disabled="isDragging"
      :selected="selected"
      :focus-on-mount="shouldFocus(slide)"
      :data-test-focused="shouldFocus(slide)"
      placeholder="Slide title"
      @click="$emit('click', $event)"
      @contextmenu="$emit('contextmenu', $event)"
      @update:title="$emit('update:title', $event)"
      @focus:title="$emit('focus:title')"
      @blur:title="$emit('blur:title')"
    >
      <template #icon>
        <DocumentTextIcon v-if="slide.type == 'P'" class="icon" />
        <IconsThreeDSlideIcon v-else-if="slide.type == 'M'" class="icon" />
        <HotspotIcon v-else-if="slide.type == 'S'" class="icon" />
        <IconsMeetingSlideIcon v-else-if="!isGridView && slide.type == 'E'" class="icon" />
        <PhotoIcon v-else-if="slide.type == 'I'" class="icon" />
        <CodeBracketIcon v-else-if="!isGridView && slide.type == 'D'" class="icon" />
        <FilmIcon v-else-if="slide.type == 'V'" class="icon" />
        <FontAwesomeIcon v-else-if="!isGridView && slide.type == 'H'" icon="heading" class="icon" />
      </template>

      <template #default>
        <img v-if="thumbnail && !'EDH'.includes(slide.type)" :src="thumbnail" draggable="false" class="object-cover w-full h-full" />
        <EditorDeckUrlSlideThumbnail v-else-if="slide.type === 'D'" :slide="slide" />
        <EditorDeckHeaderSlideThumbnail v-else-if="slide.type === 'H'" />
        <PlaceholderDeck v-else />
      </template>
      <template #top>
        <div
          v-if="slide.type === 'M' && shouldShowModelId"
          class="flex items-center mt-1 mb-2 text-xs uppercase"
          :data-testid="`model-id-${slide.model_id}`"
        >
          <CubeIcon class="flex-shrink-0 w-5 h-5 mr-1" />
          <div class="flex-grow truncate">{{ slide.model_id }} &ndash; {{ slide.model_camera?.model_title }}</div>
        </div>
      </template>
    </EditorDeckThumbnailTile>
  </li>
</template>

<script lang="ts" setup>
import type { DeepReadonly, PropType } from 'vue'
import type { Slide } from '@/entities/deck'
import PlaceholderDeck from '@/components/icons/deck-placeholder.svg'
import { CodeBracketIcon, CubeIcon, DocumentTextIcon, PhotoIcon } from '@heroicons/vue/24/solid'
import { FilmIcon } from '@heroicons/vue/24/outline'
import { isThreeDSlide } from '@/lib/type-utils'
import type { EditorSlide } from '@/composables/useEditDeck'
import IconsThreeDSlideIcon from '@/components/icons/ThreeDSlideIcon.svg'
import HotspotIcon from '@/components/icons/site/hotspot-360.svg'
import IconsMeetingSlideIcon from '@/components/icons/MeetingSlideIcon.svg'
import { getSlideThumbnail } from '~/lib/deck/get-slide-thumbnail'

const props = defineProps({
  index: {
    type: Number,
    default: 0,
  },
  isDragging: {
    type: Boolean,
    default: false,
  },
  isGridView: {
    type: Boolean,
    default: false,
  },
  slide: {
    type: Object as PropType<DeepReadonly<Slide>>,
    required: true,
  },
  selected: {
    type: Boolean,
    default: false,
  },
  shouldShowModelId: {
    type: Boolean,
    default: false,
  },
})

defineEmits([
  'click',
  'shift-click:title',
  'ctrl-click:title',
  'create-header-slide',
  'cancel-create-header-slide',
  'contextmenu',
  'update:title',
  'focus:title',
  'blur:title',
])

const thumbnail = computed(() => {
  return getSlideThumbnail(props.slide)
})

const shouldFocus = (slide: DeepReadonly<EditorSlide>) => slide.created && ['I', 'P', 'V'].includes(slide.type)
</script>

<style lang="stylus" scoped>
.icon
  @apply flex-shrink-0 mr-2
  width: 17px
  height: 17px

.deck-thumbnail-tile
  @apply rounded-md
  &__grip
    @apply opacity-80 transition-opacity ml-1
    ^[0]:hover &
      @apply opacity-100
</style>
